'use client';

import React from 'react';
import styled from 'styled-components';
import NextLink from 'next/link';
import { COLORS } from '@/constants/colors';
import type { CmsLexicalNode } from '@/lib/cms';

// Matches the styled-components used by the hand-authored blog templates
// (e.g. app/blog/toyota-tacoma-air-filter/BlogPostPage.tsx) so CMS-driven
// posts render with the same look.

const IS_BOLD = 1;
const IS_ITALIC = 1 << 1;
const IS_UNDERLINE = 1 << 3;

function renderText(node: CmsLexicalNode, key: React.Key): React.ReactNode {
  const text = String(node.text ?? '');
  const format = Number(node.format ?? 0);

  let content: React.ReactNode = text;
  if (format & IS_BOLD) content = <strong>{content}</strong>;
  if (format & IS_ITALIC) content = <em>{content}</em>;
  if (format & IS_UNDERLINE) content = <u>{content}</u>;

  return <React.Fragment key={key}>{content}</React.Fragment>;
}

function renderChildren(children: CmsLexicalNode[] | undefined): React.ReactNode {
  if (!children) return null;
  return children.map((child, i) => renderNode(child, i));
}

function renderNode(node: CmsLexicalNode, key: React.Key): React.ReactNode {
  switch (node.type) {
    case 'text':
      return renderText(node, key);
    case 'paragraph':
      return <Paragraph key={key}>{renderChildren(node.children)}</Paragraph>;
    case 'heading': {
      const tag = (node.tag as string) || 'h2';
      if (tag === 'h3') return <SubHeading key={key}>{renderChildren(node.children)}</SubHeading>;
      if (tag === 'h4') return <SubHeading as="h4" key={key}>{renderChildren(node.children)}</SubHeading>;
      return <SectionHeading key={key}>{renderChildren(node.children)}</SectionHeading>;
    }
    case 'link': {
      const fields = (node.fields as { url?: string; newTab?: boolean } | undefined) ?? {};
      if (!fields.url) return renderChildren(node.children);
      return (
        <InlineLink key={key} href={fields.url} target={fields.newTab ? '_blank' : undefined} rel={fields.newTab ? 'noopener noreferrer' : undefined}>
          {renderChildren(node.children)}
        </InlineLink>
      );
    }
    case 'list': {
      const ordered = node.listType === 'number';
      const ListTag = ordered ? OrderedList : BulletList;
      const Item = ordered ? OrderedItem : BulletItem;
      return (
        <ListTag key={key}>
          {(node.children ?? []).map((child, i) => (
            <Item key={i}>{renderChildren(child.children)}</Item>
          ))}
        </ListTag>
      );
    }
    default:
      return renderChildren(node.children);
  }
}

export default function LexicalRichText({ root }: { root: CmsLexicalNode }) {
  return <>{renderChildren(root.children)}</>;
}

const InlineLink = styled(NextLink)`
  color: ${COLORS.brandGreen};
  text-decoration: underline;
`;

const SectionHeading = styled.h2`
  font-family: Lato, sans-serif;
  font-size: 32px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 48px 0 24px;
  line-height: 1.3;

  @media (max-width: 767px) {
    font-size: 26px;
    margin: 40px 0 20px;
  }
`;

const SubHeading = styled.h3`
  font-family: Lato, sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 32px 0 12px;
  line-height: 1.4;
`;

const Paragraph = styled.p`
  font-family: Lato, sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #555;
  line-height: 1.7;
  margin: 0 0 20px;

  @media (max-width: 767px) {
    font-size: 15px;
  }
`;

const BulletList = styled.ul`
  margin: 20px 0;
  padding-left: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const OrderedList = styled.ol`
  margin: 20px 0;
  padding-left: 0;
  list-style: none;
  counter-reset: step-counter;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const listItemBase = `
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-family: Lato, sans-serif;
  font-size: 16px;
  color: #555;
  line-height: 1.6;

  @media (max-width: 767px) {
    font-size: 15px;
  }
`;

const BulletItem = styled.li`
  ${listItemBase}

  &::before {
    content: '•';
    color: ${COLORS.brandGreen};
    font-weight: 700;
    flex-shrink: 0;
  }
`;

const OrderedItem = styled.li`
  ${listItemBase}
  counter-increment: step-counter;

  &::before {
    content: counter(step-counter);
    min-width: 28px;
    height: 28px;
    background: ${COLORS.brandGreen};
    color: ${COLORS.white};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 700;
    flex-shrink: 0;
  }
`;
